// Lightweight, dependency-free syntax highlighter for fenced code blocks.
//
// A pure function `highlightCode(code, lang)` → HTML string of `.tok-*` spans,
// used by the reader's decorateCodeBlocks pass (markdown.ts). It generalises the
// small regex tokenizer the API sample panel already ships: escape HTML first
// (so injecting the result via innerHTML can never introduce a tag — XSS-safe),
// then run one combined, ordered alternation per language and wrap each match in
// a `tok-{kw,str,num,com,punct}` span.
//
// Highlighting is deliberately approximate (keyword / string / number / comment /
// punctuation buckets, not a real parser) and covers the languages common in the
// docs corpus — js/ts(x), json, bash/shell, yaml, html/xml — with a sensible
// generic fallback for everything else. This intentionally does NOT pull in
// shiki/prism/highlight.js.

// Token buckets, emitted as `<span class="tok-*">`. Styled in public.css for both
// the reader's light/dark code cards and the (dark) API sample panel.
type TokenClass = "kw" | "str" | "num" | "com" | "punct";

interface Rule {
  cls: TokenClass;
  // Each rule's pattern MUST NOT contain a capturing group — the tokenizer wraps
  // every rule in exactly one group and maps group index → class, so an internal
  // `(...)` would break that alignment (use `(?:...)`, lookarounds, char classes).
  re: RegExp;
}

// Only escape the three tag-significant characters — NOT quotes — so the string
// rules below can still see their `"`/`'` delimiters in the escaped text.
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ── Shared token fragments (no capturing groups) ─────────────────────────────
const C_LINE = /\/\/[^\n]*/;
const C_BLOCK = /\/\*[\s\S]*?\*\//;
const HASH = /#[^\n]*/;
const STR_D = /"(?:[^"\\\n]|\\.)*"/;
const STR_S = /'(?:[^'\\\n]|\\.)*'/;
const STR_TICK = /`(?:[^`\\]|\\.)*`/;
const NUM = /\b(?:0x[\da-fA-F]+|\d+(?:\.\d+)?)\b/;
const PUNCT = /[{}[\]()]/;

const kw = (words: string): RegExp => new RegExp(`\\b(?:${words})\\b`);

// ── Per-language rule sets (ordered: comments & strings before keywords) ─────
const JS_KW =
  "abstract|as|async|await|break|case|catch|class|const|continue|debugger|declare|" +
  "default|delete|do|else|enum|export|extends|false|finally|for|from|function|get|" +
  "if|implements|import|in|instanceof|interface|is|keyof|let|namespace|new|null|of|" +
  "private|protected|public|readonly|return|satisfies|set|static|super|switch|this|" +
  "throw|true|try|type|typeof|undefined|var|void|while|yield";

const JS: Rule[] = [
  { cls: "com", re: C_BLOCK },
  { cls: "com", re: C_LINE },
  { cls: "str", re: STR_TICK },
  { cls: "str", re: STR_D },
  { cls: "str", re: STR_S },
  { cls: "kw", re: kw(JS_KW) },
  { cls: "num", re: NUM },
  { cls: "punct", re: PUNCT },
];

const JSON_RULES: Rule[] = [
  { cls: "com", re: C_BLOCK },
  { cls: "com", re: C_LINE },
  { cls: "str", re: STR_D },
  { cls: "str", re: STR_S },
  { cls: "kw", re: kw("true|false|null") },
  { cls: "num", re: NUM },
  { cls: "punct", re: /[{}[\]:,]/ },
];

const BASH_KW =
  "if|then|else|elif|fi|for|while|until|do|done|case|esac|in|function|return|select|" +
  "time|coproc|export|local|readonly|declare|typeset|unset|set|shift|source|alias|" +
  "eval|exec|trap|echo|printf|read|cd|pushd|popd|exit|true|false";

const BASH: Rule[] = [
  { cls: "com", re: HASH },
  { cls: "str", re: STR_D },
  { cls: "str", re: STR_S },
  // Shell variables (`$foo`, `${foo}`, `$1`, `$@`, `$#`, …) — coloured with the
  // keyword bucket; approximate but reads well.
  { cls: "kw", re: /\$\{[^}]*\}|\$\w+|\$[@*#?$!0-9-]/ },
  { cls: "kw", re: kw(BASH_KW) },
  { cls: "num", re: NUM },
];

const YAML: Rule[] = [
  { cls: "com", re: HASH },
  { cls: "str", re: STR_D },
  { cls: "str", re: STR_S },
  // Mapping keys — only at line start (the `m` flag makes `^` match each line),
  // so a `key:`-shaped substring inside a value (e.g. a URL) is not mis-coloured.
  { cls: "kw", re: /^[ \t]*(?:- )?[\w.$-]+(?=[ \t]*:)/ },
  { cls: "kw", re: kw("true|false|null|True|False|Null|yes|no|on|off") },
  { cls: "num", re: NUM },
];

// HTML/XML rules run over ALREADY-ESCAPED text, so tags read as `&lt;…&gt;`.
const HTML: Rule[] = [
  { cls: "com", re: /&lt;!--[\s\S]*?--&gt;/ },
  { cls: "str", re: /"[^"\n]*"|'[^'\n]*'/ },
  // Tag/element name right after the (escaped) opening `<` or `</`.
  { cls: "kw", re: /(?<=&lt;\/?)[a-zA-Z][\w:-]*/ },
  { cls: "punct", re: /&lt;\/?|\/?&gt;/ },
];

const GENERIC: Rule[] = [
  { cls: "com", re: C_BLOCK },
  { cls: "com", re: C_LINE },
  { cls: "com", re: HASH },
  { cls: "str", re: STR_D },
  { cls: "str", re: STR_S },
  { cls: "num", re: NUM },
  { cls: "punct", re: PUNCT },
];

const LANGS: Record<string, Rule[]> = {};
const register = (rules: Rule[], ...names: string[]): void => {
  for (const n of names) LANGS[n] = rules;
};
register(JS, "js", "javascript", "mjs", "cjs", "jsx", "tsx", "ts", "typescript");
register(JSON_RULES, "json", "jsonc", "json5");
register(BASH, "sh", "bash", "shell", "zsh", "console", "shell-session", "shellsession");
register(YAML, "yaml", "yml");
register(HTML, "html", "xml", "svg", "xhtml");

// Compile a rule set into one combined global regex + a class lookup, cached per
// rule-set array so repeated renders don't rebuild the pattern.
const compiledCache = new WeakMap<Rule[], { re: RegExp; classes: TokenClass[] }>();
function compile(rules: Rule[]): { re: RegExp; classes: TokenClass[] } {
  let c = compiledCache.get(rules);
  if (!c) {
    const source = rules.map((r) => `(${r.re.source})`).join("|");
    c = { re: new RegExp(source, "gm"), classes: rules.map((r) => r.cls) };
    compiledCache.set(rules, c);
  }
  return c;
}

function tokenize(code: string, rules: Rule[]): string {
  const { re, classes } = compile(rules);
  // String.replace resets a global regex's lastIndex, so a shared cached RegExp
  // is safe to reuse across calls.
  return escapeHtml(code).replace(re, (...args) => {
    // args = [match, group1, …, groupN, offset, string]; exactly one group is a
    // string (matched), the rest are undefined — its index selects the class.
    for (let i = 0; i < classes.length; i++) {
      const g = args[i + 1];
      if (typeof g === "string") return `<span class="tok-${classes[i]}">${g}</span>`;
    }
    return String(args[0]);
  });
}

// Whether a language token has a dedicated (non-generic) rule set — handy for
// callers that want to know if highlighting will be language-aware.
export function hasLanguage(lang: string | null | undefined): boolean {
  return !!lang && Object.prototype.hasOwnProperty.call(LANGS, lang.toLowerCase());
}

// Highlight `code` for `lang`, returning HTML of `.tok-*` spans. HTML is always
// escaped first (XSS-safe); an unknown/empty language uses the generic fallback.
export function highlightCode(code: string, lang?: string | null): string {
  const rules = (lang && LANGS[lang.toLowerCase()]) || GENERIC;
  return tokenize(code, rules);
}
